import type { RecordFilters } from "@/lib/query-keys";
import type { ProjectRecord } from "@/types/api";

/** `date_to` is inclusive of the whole end day. */
const END_OF_DAY_MS = 86_400_000;

/**
 * True when any non-search filter is set. `search` is excluded because it is
 * server-only semantic search and is never applied client-side.
 */
export function hasActiveRecordFilter(filters: RecordFilters): boolean {
  return (
    (filters.tags?.length ?? 0) > 0 ||
    (filters.created_by?.length ?? 0) > 0 ||
    (filters.plan?.length ?? 0) > 0 ||
    filters.hasTags != null ||
    filters.has_pin != null ||
    filters.date_from != null ||
    filters.date_to != null
  );
}

/**
 * Apply record filters client-side — used offline, where the per-filter
 * server query has no cached data, and by the plan viewer. Covers tags,
 * creator, plan, pin presence, and the `created_at` date range.
 *
 * `search` is deliberately not handled: it is server-side semantic search
 * with no client-side equivalent (the offline UI hides the search input).
 * Returns the input array unchanged when no filter is active.
 */
export function filterRecords(
  records: ProjectRecord[],
  filters: RecordFilters,
): ProjectRecord[] {
  if (!hasActiveRecordFilter(filters)) return records;

  const tagFilter =
    filters.tags && filters.tags.length > 0 ? filters.tags : null;
  const creatorFilter =
    filters.created_by && filters.created_by.length > 0
      ? new Set(filters.created_by)
      : null;
  const fromTs = filters.date_from
    ? new Date(filters.date_from).getTime()
    : null;
  const toTs = filters.date_to
    ? new Date(filters.date_to).getTime() + END_OF_DAY_MS
    : null;

  const planFilter =
    filters.plan && filters.plan.length > 0 ? new Set(filters.plan) : null;

  return records.filter((record) => {
    if (planFilter && (record.plan == null || !planFilter.has(record.plan))) {
      return false;
    }

    if (filters.hasTags === true && record.tags.length === 0) return false;
    if (filters.hasTags === false && record.tags.length > 0) return false;

    if (tagFilter && !record.tags.some((t) => tagFilter.includes(t))) {
      return false;
    }

    if (creatorFilter && !creatorFilter.has(record.created_by.id)) {
      return false;
    }

    if (filters.has_pin === true && record.pin_x == null) return false;
    if (filters.has_pin === false && record.pin_x != null) return false;

    if (fromTs != null || toTs != null) {
      const ts = record.created_at
        ? new Date(record.created_at).getTime()
        : null;
      if (ts == null) return false;
      if (fromTs != null && ts < fromTs) return false;
      if (toTs != null && ts > toTs) return false;
    }

    return true;
  });
}
